import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { useUserIdParamsOrThrow } from "../useUserIdParamsOrThrow.ts";
import { formatDate } from "../../UI/Utils/formatDate.ts";
import { useConfirm } from "../../UI/Feedback/useConfirm.ts";
import { useUser1on1ClassroomPlans } from "./useUser1on1ClassroomPlans.ts";
import { FullPageLoading } from "../../UI/Loading/FullPageLoading.tsx";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../../UI/Layout/ScreenTitleBlock.tsx";
import { useUserById } from "../useUserById.ts";
import { BreadCrumbsWrapper } from "../../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../../UI/Layout/BreadCrumbsItem.tsx";
import { useToAllUsers } from "../useToAllUsers.ts";
import { InfoFeedback } from "../../UI/Feedback/InfoFeedback.tsx";
import { Card } from "../../UI/Card.tsx";
import { PrimaryButton } from "../../UI/PrimaryButton.tsx";
import { OutlinedButton } from "../../UI/OutlinedButton.tsx";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  BusinessEnglishObjective,
  BulkClassRoomCreationRequestStatus,
  OneOnOneLessonType,
  SpeakingClassLevel,
  User1on1ClassroomPlanGraphqlDto,
  TeacherSubstitutionResultGraphqlDto,
} from "../../gql/graphql.ts";
import { DateTimeInput, Select, SelectOption } from "@normasteaching/norma-ui";
import { useUpdateUser1on1ClassroomPlanSpeakingLevel } from "./useUpdateUser1on1ClassroomPlanSpeakingLevel.ts";
import { useUpdateUser1on1ClassroomPlanLessonType } from "./useUpdateUser1on1ClassroomPlanLessonType.ts";
import {
  BookedClassRoom,
  useGetBookedClassRoomsForPlan,
} from "./useGetBookedClassRoomsForPlan.ts";
import { useDelete1on1PlanClassRoomSilently } from "./useDelete1on1PlanClassRoomSilently.ts";
import { useDeleteAll1on1PlanFutureClassRooms } from "./useDeleteAll1on1PlanFutureClassRooms.ts";
import { useAddLessonsTo1on1Plan } from "./useAddLessonsTo1on1Plan.ts";
import { useGetBulkClassRoomCreationRequest } from "../../BulkClassRoom/useGetBulkClassRoomCreationRequest.ts";
import {
  CheckedSlot,
  useCheck1on1PlanSlotConflicts,
} from "./useCheck1on1PlanSlotConflicts.ts";
import { useTry1on1PlanSlotTeacherSubstitution } from "./useTry1on1PlanSlotTeacherSubstitution.ts";
import { SubstitutionResultModal } from "../../BulkClassRoom/SubstitutionResultModal.tsx";
import { useState } from "react";
import { PrimaryLoadingIndicator } from "../../UI/Loading/PrimaryLoadingIndicator.tsx";
import { LoaderSize } from "../../UI/Loading/LoaderSize.tsx";

dayjs.extend(utc);
dayjs.extend(timezone);

const PLAN_TIMEZONE = "Europe/Rome";

type PlanLocalState = {
  oneOnOneLessonType: OneOnOneLessonType | null;
  businessEnglishObjective: BusinessEnglishObjective | null;
  speakingLevel: SpeakingClassLevel | null;
};

type SlotDraft = {
  key: string;
  startUtc: Date;
};

function planToLocalState(
  plan: User1on1ClassroomPlanGraphqlDto,
): PlanLocalState {
  return {
    oneOnOneLessonType: plan.oneOnOneLessonType ?? null,
    businessEnglishObjective: plan.businessEnglishObjective ?? null,
    speakingLevel: plan.speakingLevel ?? null,
  };
}

function isDirty(
  local: PlanLocalState,
  plan: User1on1ClassroomPlanGraphqlDto,
): boolean {
  return (
    local.oneOnOneLessonType !== (plan.oneOnOneLessonType ?? null) ||
    local.businessEnglishObjective !==
      (plan.businessEnglishObjective ?? null) ||
    local.speakingLevel !== (plan.speakingLevel ?? null)
  );
}

let slotKeyCounter = 0;

function getNextRoundHour(): Date {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  if (d.getHours() < 7) d.setHours(7);
  if (d.getHours() > 22) {
    d.setDate(d.getDate() + 1);
    d.setHours(7);
  }
  return d;
}

const lessonTypeOptions: SelectOption<OneOnOneLessonType>[] = Object.values(
  OneOnOneLessonType,
).map((lessonType) => ({ id: lessonType, name: lessonType }));

const businessEnglishObjectiveOptions: SelectOption<BusinessEnglishObjective>[] =
  Object.values(BusinessEnglishObjective).map((objective) => ({
    id: objective,
    name: objective,
  }));

const speakingLevelOptions: SelectOption<SpeakingClassLevel>[] = Object.values(
  SpeakingClassLevel,
).map((speakingLevel) => ({ id: speakingLevel, name: speakingLevel }));

export const AllUser1on1ClassroomPlansScreenPath =
  "/users/:userId/1on1-classroom-plans";

// Sub-component isolato per il polling dello stato della richiesta
function PendingRequestBanner({
  requestId,
  onCompleted,
}: {
  requestId: string;
  onCompleted: () => void;
}) {
  const { request } = useGetBulkClassRoomCreationRequest(requestId);

  if (request?.status === BulkClassRoomCreationRequestStatus.Completed) {
    onCompleted();
    return null;
  }

  return (
    <div
      className={
        "flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded px-3 py-2"
      }
    >
      <PrimaryLoadingIndicator size={LoaderSize.Small} />
      <span>
        Creazione in corso ({request?.totalClassRoomsToCreate ?? "—"} lezioni)…
      </span>
    </div>
  );
}

export function AllUser1on1ClassroomPlansScreen() {
  const { userId } = useUserIdParamsOrThrow();
  const {
    plans,
    loading: loadingPlans,
    refetch,
  } = useUser1on1ClassroomPlans(userId);
  const { user, loading: loadingUser } = useUserById(userId);
  const loading = loadingPlans || loadingUser;

  const { updateSpeakingLevel } = useUpdateUser1on1ClassroomPlanSpeakingLevel();
  const { updateLessonType } = useUpdateUser1on1ClassroomPlanLessonType();
  const {
    loadClassRooms,
    classRooms,
    teacherId,
    loading: loadingClassRooms,
  } = useGetBookedClassRoomsForPlan();
  const { deleteClassRoom, loading: deletingClassRoom } =
    useDelete1on1PlanClassRoomSilently();
  const { deleteAll, loading: deletingAll } =
    useDeleteAll1on1PlanFutureClassRooms();
  const askConfirmation = useConfirm();
  const { addLessons, loading: addingLessons } = useAddLessonsTo1on1Plan();
  const { checkConflicts, loading: checkingConflicts } =
    useCheck1on1PlanSlotConflicts();
  const { trySubstitution, loading: substituting } =
    useTry1on1PlanSlotTeacherSubstitution();

  const [localStates, setLocalStates] = useState<
    Record<string, PlanLocalState>
  >({});
  const [savingPlanId, setSavingPlanId] = useState<string | null>(null);
  const [expandedLessonsPlanId, setExpandedLessonsPlanId] = useState<
    string | null
  >(null);
  const [deletingClassRoomId, setDeletingClassRoomId] = useState<string | null>(
    null,
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const [addStep, setAddStep] = useState<"draft" | "conflicts">("draft");
  const [slotDrafts, setSlotDrafts] = useState<SlotDraft[]>([]);
  const [showAddSlotModal, setShowAddSlotModal] = useState(false);
  const [newSlotStart, setNewSlotStart] = useState<Date>(getNextRoundHour());
  const [checkedSlots, setCheckedSlots] = useState<CheckedSlot[]>([]);
  const [addError, setAddError] = useState<string | null>(null);
  const [substitutionResult, setSubstitutionResult] =
    useState<TeacherSubstitutionResultGraphqlDto | null>(null);
  const [showSubstitutionModal, setShowSubstitutionModal] = useState(false);
  // requestId dell'ultima richiesta in corso, per mostrare il banner di polling
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);

  const { toAllUsers } = useToAllUsers();

  if (loading) return <FullPageLoading />;
  if (!user) return null;

  function getLocalState(
    plan: User1on1ClassroomPlanGraphqlDto,
  ): PlanLocalState {
    return localStates[plan.id] ?? planToLocalState(plan);
  }

  function setLocalState(
    plan: User1on1ClassroomPlanGraphqlDto,
    patch: Partial<PlanLocalState>,
  ) {
    setLocalStates((prev) => ({
      ...prev,
      [plan.id]: { ...(prev[plan.id] ?? planToLocalState(plan)), ...patch },
    }));
  }

  async function handleSave(plan: User1on1ClassroomPlanGraphqlDto) {
    const local = getLocalState(plan);
    setSavingPlanId(plan.id);
    try {
      if (
        local.speakingLevel !== (plan.speakingLevel ?? null) &&
        local.speakingLevel
      ) {
        await updateSpeakingLevel(plan.id, local.speakingLevel);
      }
      if (
        local.oneOnOneLessonType !== (plan.oneOnOneLessonType ?? null) ||
        local.businessEnglishObjective !==
          (plan.businessEnglishObjective ?? null)
      ) {
        if (local.oneOnOneLessonType) {
          const businessEnglishObjective =
            local.businessEnglishObjective ??
            BusinessEnglishObjective.Presentazioni;
          await updateLessonType(
            plan.id,
            local.oneOnOneLessonType,
            local.oneOnOneLessonType === OneOnOneLessonType.BusinessEnglish
              ? businessEnglishObjective
              : undefined,
          );
        }
      }
      await refetch();
      setLocalStates((prev) => {
        const next = { ...prev };
        delete next[plan.id];
        return next;
      });
    } finally {
      setSavingPlanId(null);
    }
  }

  function handleToggleLessons(planId: string) {
    if (expandedLessonsPlanId === planId) {
      setExpandedLessonsPlanId(null);
      setShowAddForm(false);
    } else {
      setExpandedLessonsPlanId(planId);
      setShowAddForm(false);
      loadClassRooms(planId);
    }
  }

  async function handleDeleteClassRoom(classRoomId: string, planId: string) {
    setDeletingClassRoomId(classRoomId);
    try {
      await deleteClassRoom(classRoomId);
      loadClassRooms(planId);
    } finally {
      setDeletingClassRoomId(null);
    }
  }

  async function handleDeleteAll(planId: string) {
    if (
      !(await askConfirmation({
        title: "Eliminare tutte le lezioni future?",
        message: "L'operazione è irreversibile.",
        confirmLabel: "Elimina",
        isDestructive: true,
      }))
    )
      return;
    await deleteAll(planId);
    loadClassRooms(planId);
  }

  async function handleCheckConflicts(plan: User1on1ClassroomPlanGraphqlDto) {
    setAddError(null);
    if (!teacherId) {
      setAddError(
        "Impossibile determinare il teacher: non ci sono lezioni associate al piano.",
      );
      return;
    }
    const slots = slotDrafts.map((s) => ({
      startUtc: s.startUtc.toISOString(),
      endUtc: new Date(s.startUtc.getTime() + 60 * 60 * 1000).toISOString(),
    }));
    for (const slot of slots) {
      if (new Date(slot.startUtc) >= plan.endDateUtc) {
        setAddError("Uno o più slot superano la scadenza del piano.");
        return;
      }
    }
    try {
      const result = await checkConflicts({
        planId: plan.id,
        teacherId,
        slots,
      });
      setCheckedSlots(result);
      setAddStep("conflicts");
    } catch (e) {
      setAddError(
        e instanceof Error
          ? e.message
          : "Errore durante il controllo conflitti.",
      );
    }
  }

  function handleAddSlotFromModal() {
    const startUtc = new Date(newSlotStart);
    setSlotDrafts((prev) => [
      ...prev,
      { key: `slot-${slotKeyCounter++}`, startUtc },
    ]);
    setShowAddSlotModal(false);
  }

  async function handleConfirmLessons(plan: User1on1ClassroomPlanGraphqlDto) {
    setAddError(null);
    if (!teacherId) return;
    try {
      const requestId = await addLessons({
        planId: plan.id,
        teacherId,
        slots: checkedSlots.map((s) => ({
          startUtc: s.startDateUtc,
          endUtc: s.endDateUtc,
        })),
      });
      setPendingRequestId(requestId);
      setShowAddForm(false);
      setAddStep("draft");
      setSlotDrafts([]);
      setCheckedSlots([]);
    } catch (e) {
      setAddError(
        e instanceof Error
          ? e.message
          : "Errore durante la creazione delle lezioni.",
      );
    }
  }

  async function handleAutoSubstitution(plan: User1on1ClassroomPlanGraphqlDto) {
    if (!teacherId) return;
    try {
      const result = await trySubstitution({
        planId: plan.id,
        teacherId,
        slots: checkedSlots.map((s) => ({
          startUtc: s.startDateUtc,
          endUtc: s.endDateUtc,
        })),
      });
      setSubstitutionResult(result);
      setShowSubstitutionModal(true);
    } catch (e) {
      setAddError(
        e instanceof Error
          ? e.message
          : "Errore durante la risoluzione automatica.",
      );
    }
  }

  async function handleSubstitutionModalClose(
    plan: User1on1ClassroomPlanGraphqlDto,
  ) {
    setShowSubstitutionModal(false);
    if (!teacherId) return;
    // Aggiorna i conflitti dopo la sostituzione
    const updated = await checkConflicts({
      planId: plan.id,
      teacherId,
      slots: checkedSlots.map((s) => ({
        startUtc: s.startDateUtc,
        endUtc: s.endDateUtc,
      })),
    });
    setCheckedSlots(updated);
  }

  function handleCreationCompleted(planId: string) {
    setPendingRequestId(null);
    loadClassRooms(planId);
  }

  return (
    <>
      <Layout>
        <Card>
          <div>
            <ScreenTitleBlock
              title={`${user.givenName}'s 1 on 1 Plans`}
              breadCrumbs={
                <BreadCrumbsWrapper>
                  <BreadCrumbsItem onClick={toAllUsers} title={"Users"} />
                  <BreadCrumbsItem title={"1 on 1 Plans"} />
                </BreadCrumbsWrapper>
              }
            />
          </div>
        </Card>
        <div className={"mt-4"}>
          {plans.length > 0 ? (
            <div className={"space-y-3"}>
              {plans.map((plan: User1on1ClassroomPlanGraphqlDto) => {
                const local = getLocalState(plan);
                const isBusinessEnglish =
                  local.oneOnOneLessonType ===
                  OneOnOneLessonType.BusinessEnglish;
                const dirty = isDirty(local, plan);
                const saving = savingPlanId === plan.id;
                const lessonsExpanded = expandedLessonsPlanId === plan.id;
                const planExpiry = dayjs(plan.endDateUtc).tz(PLAN_TIMEZONE);

                return (
                  <Card key={plan.id}>
                    <div className={"p-4 space-y-2"}>
                      <div className={"flex justify-between items-center"}>
                        <h3 className={"text-lg font-semibold"}>
                          Plan {plan.id}
                        </h3>
                        <span className={"text-sm text-gray-500"}>
                          Created: {formatDate(plan.createdAt)}
                        </span>
                      </div>

                      <div className={"grid grid-cols-2 gap-4 text-sm"}>
                        <div>
                          <span className={"font-medium"}>Start Date:</span>{" "}
                          {formatDate(plan.startDateUtc)}
                        </div>
                        <div>
                          <span className={"font-medium"}>Plan Expiry:</span>{" "}
                          {formatDate(planExpiry)}
                        </div>
                        <div>
                          <span className={"font-medium"}>
                            Last Lesson Date:
                          </span>{" "}
                          {plan.lastLessonDateUtc
                            ? formatDate(plan.lastLessonDateUtc)
                            : "—"}
                        </div>
                        <div>
                          <span className={"font-medium"}>
                            Lessons per Week:
                          </span>{" "}
                          {plan.lessonsPerWeek}
                        </div>
                        <div>
                          <span className={"font-medium"}>
                            Number of Weeks:
                          </span>{" "}
                          {plan.numberOfWeeks}
                        </div>
                        <div className={"col-span-2"}>
                          <span className={"font-medium"}>Lesson Type:</span>
                          <div className={"mt-1"}>
                            <Select<OneOnOneLessonType>
                              disabled={saving}
                              value={local.oneOnOneLessonType ?? undefined}
                              options={lessonTypeOptions}
                              onChange={(lessonType) => {
                                if (!lessonType) return;
                                setLocalState(plan, {
                                  oneOnOneLessonType: lessonType,
                                  businessEnglishObjective:
                                    lessonType ===
                                    OneOnOneLessonType.BusinessEnglish
                                      ? (local.businessEnglishObjective ??
                                        BusinessEnglishObjective.Presentazioni)
                                      : null,
                                });
                              }}
                            />
                          </div>
                        </div>
                        {isBusinessEnglish && (
                          <div className={"col-span-2"}>
                            <span className={"font-medium"}>
                              Business English Objective:
                            </span>
                            <div className={"mt-1"}>
                              <Select<BusinessEnglishObjective>
                                disabled={saving}
                                value={
                                  local.businessEnglishObjective ??
                                  BusinessEnglishObjective.Presentazioni
                                }
                                options={businessEnglishObjectiveOptions}
                                onChange={(objective) => {
                                  if (!objective) return;
                                  setLocalState(plan, {
                                    businessEnglishObjective: objective,
                                  });
                                }}
                              />
                            </div>
                          </div>
                        )}
                        <div className={"col-span-2"}>
                          <span className={"font-medium"}>Speaking Level:</span>
                          <div className={"mt-1"}>
                            <Select<SpeakingClassLevel>
                              disabled={saving}
                              value={local.speakingLevel ?? undefined}
                              options={speakingLevelOptions}
                              onChange={(speakingLevel) => {
                                if (!speakingLevel) return;
                                setLocalState(plan, { speakingLevel });
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className={"flex justify-end pt-2"}>
                        <PrimaryButton
                          label={saving ? "Saving..." : "Save"}
                          loading={saving}
                          disabled={!dirty || saving}
                          onClick={() => handleSave(plan)}
                        />
                      </div>

                      {/* Sezione gestione lezioni */}
                      <div className={"border-t pt-3 mt-1"}>
                        <OutlinedButton
                          label={
                            lessonsExpanded
                              ? "Nascondi lezioni prenotate"
                              : "Gestisci lezioni prenotate"
                          }
                          onClick={() => handleToggleLessons(plan.id)}
                        />

                        {lessonsExpanded && (
                          <div className={"mt-3 space-y-3"}>
                            {loadingClassRooms ? (
                              <LoadingRows rows={3} />
                            ) : (
                              <>
                                {/* Banner polling creazione in corso */}
                                {pendingRequestId && (
                                  <PendingRequestBanner
                                    requestId={pendingRequestId}
                                    onCompleted={() =>
                                      handleCreationCompleted(plan.id)
                                    }
                                  />
                                )}

                                {/* Lista lezioni future */}
                                {classRooms.length === 0 &&
                                !pendingRequestId ? (
                                  <p className={"text-sm text-gray-500"}>
                                    Nessuna lezione futura prenotata.
                                  </p>
                                ) : (
                                  <div className={"space-y-2"}>
                                    {classRooms.map(
                                      (classRoom: BookedClassRoom) => {
                                        const start = dayjs(
                                          classRoom.startedAtUtc,
                                        ).tz(classRoom.classRoomTimezone);
                                        const end = dayjs(
                                          classRoom.endedAtUtc,
                                        ).tz(classRoom.classRoomTimezone);
                                        const isDeleting =
                                          deletingClassRoomId === classRoom.id;
                                        return (
                                          <div
                                            key={classRoom.id}
                                            className={
                                              "flex items-center justify-between rounded border px-3 py-2 text-sm"
                                            }
                                          >
                                            <span>
                                              {formatDate(start, {
                                                withWeekday: true,
                                              })}{" "}
                                              — {start.format("HH:mm")}–
                                              {end.format("HH:mm")}{" "}
                                              <span className={"text-gray-400"}>
                                                ({classRoom.classRoomTimezone})
                                              </span>
                                            </span>
                                            <button
                                              disabled={
                                                isDeleting ||
                                                !!deletingClassRoom
                                              }
                                              onClick={() =>
                                                handleDeleteClassRoom(
                                                  classRoom.id,
                                                  plan.id,
                                                )
                                              }
                                              className={
                                                "ml-4 text-red-500 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium"
                                              }
                                            >
                                              {isDeleting
                                                ? "Rimozione..."
                                                : "Rimuovi"}
                                            </button>
                                          </div>
                                        );
                                      },
                                    )}
                                  </div>
                                )}

                                {/* Azioni bulk */}
                                <div
                                  className={
                                    "flex items-center gap-3 flex-wrap"
                                  }
                                >
                                  {classRooms.length > 0 && (
                                    <button
                                      disabled={deletingAll}
                                      onClick={() => handleDeleteAll(plan.id)}
                                      className={
                                        "text-red-500 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium border border-red-300 rounded px-3 py-1"
                                      }
                                    >
                                      {deletingAll
                                        ? "Eliminazione in corso..."
                                        : "Elimina tutte le lezioni future"}
                                    </button>
                                  )}
                                  {!pendingRequestId && (
                                    <button
                                      onClick={() => {
                                        const opening = !showAddForm;
                                        setShowAddForm(opening);
                                        if (opening) {
                                          setAddStep("draft");
                                          setAddError(null);
                                          setSlotDrafts([]);
                                          setCheckedSlots([]);
                                        }
                                      }}
                                      className={
                                        "text-primary hover:text-primary/80 text-sm font-medium border border-primary rounded px-3 py-1"
                                      }
                                    >
                                      {showAddForm
                                        ? "Annulla"
                                        : "+ Aggiungi lezioni"}
                                    </button>
                                  )}
                                </div>

                                {/* Form aggiungi lezioni */}
                                {showAddForm && (
                                  <div
                                    className={
                                      "rounded border border-dashed border-primary p-4 space-y-3 bg-slate-50"
                                    }
                                  >
                                    {/* Step 1 — selezione slot */}
                                    {addStep === "draft" && (
                                      <>
                                        <p className={"text-sm font-medium"}>
                                          Nuove lezioni — fuso orario:{" "}
                                          <span className={"font-normal"}>
                                            {PLAN_TIMEZONE}
                                          </span>{" "}
                                          — max:{" "}
                                          <span className={"font-normal"}>
                                            {formatDate(planExpiry)}
                                          </span>
                                        </p>

                                        <div className={"space-y-2"}>
                                          {slotDrafts.length === 0 ? (
                                            <p
                                              className={
                                                "text-sm text-gray-500"
                                              }
                                            >
                                              Nessuno slot selezionato.
                                            </p>
                                          ) : (
                                            slotDrafts.map((slot) => {
                                              const start = dayjs(
                                                slot.startUtc,
                                              ).tz(PLAN_TIMEZONE);
                                              const end = dayjs(slot.startUtc)
                                                .tz(PLAN_TIMEZONE)
                                                .add(1, "hour");
                                              return (
                                                <div
                                                  key={slot.key}
                                                  className={
                                                    "flex items-center justify-between rounded border px-3 py-2 text-sm bg-white"
                                                  }
                                                >
                                                  <span>
                                                    {formatDate(start, {
                                                      withWeekday: true,
                                                    })}{" "}
                                                    — {start.format("HH:mm")}–
                                                    {end.format("HH:mm")}
                                                  </span>
                                                  <button
                                                    onClick={() =>
                                                      setSlotDrafts((prev) =>
                                                        prev.filter(
                                                          (s) =>
                                                            s.key !== slot.key,
                                                        ),
                                                      )
                                                    }
                                                    className={
                                                      "text-red-400 hover:text-red-600 text-lg leading-none"
                                                    }
                                                    title="Rimuovi slot"
                                                  >
                                                    ×
                                                  </button>
                                                </div>
                                              );
                                            })
                                          )}
                                        </div>

                                        <div className={"w-fit"}>
                                          <OutlinedButton
                                            label={"Aggiungi slot"}
                                            onClick={() => {
                                              setNewSlotStart(
                                                getNextRoundHour(),
                                              );
                                              setShowAddSlotModal(true);
                                            }}
                                          />
                                        </div>

                                        {addError && (
                                          <p className={"text-red-500 text-sm"}>
                                            {addError}
                                          </p>
                                        )}

                                        <PrimaryButton
                                          label={
                                            checkingConflicts
                                              ? "Controllo in corso..."
                                              : "Controlla conflitti"
                                          }
                                          loading={checkingConflicts}
                                          disabled={
                                            checkingConflicts ||
                                            slotDrafts.length === 0
                                          }
                                          onClick={() =>
                                            handleCheckConflicts(plan)
                                          }
                                        />
                                      </>
                                    )}

                                    {/* Step 2 — risultato conflitti */}
                                    {addStep === "conflicts" &&
                                      (() => {
                                        const hasConflicts = checkedSlots.some(
                                          (s) =>
                                            s.teacherConflictingClass ||
                                            s.userConflictingClass,
                                        );
                                        return (
                                          <>
                                            <p
                                              className={"text-sm font-medium"}
                                            >
                                              Verifica conflitti —{" "}
                                              {hasConflicts ? (
                                                <span
                                                  className={"text-red-600"}
                                                >
                                                  {
                                                    checkedSlots.filter(
                                                      (s) =>
                                                        s.teacherConflictingClass ||
                                                        s.userConflictingClass,
                                                    ).length
                                                  }{" "}
                                                  conflitto/i rilevato/i
                                                </span>
                                              ) : (
                                                <span
                                                  className={"text-green-600"}
                                                >
                                                  Nessun conflitto
                                                </span>
                                              )}
                                            </p>

                                            <div className={"space-y-2"}>
                                              {checkedSlots.map((slot, i) => {
                                                const conflict =
                                                  slot.teacherConflictingClass ||
                                                  slot.userConflictingClass;
                                                const is1on1Conflict =
                                                  !!slot.teacherConflictingClass
                                                    ?.is1on1 ||
                                                  !!slot.userConflictingClass
                                                    ?.is1on1;
                                                const start = dayjs(
                                                  slot.startDateUtc,
                                                ).tz(PLAN_TIMEZONE);
                                                const end = dayjs(
                                                  slot.endDateUtc,
                                                ).tz(PLAN_TIMEZONE);
                                                return (
                                                  <div
                                                    key={i}
                                                    className={`rounded border px-3 py-2 text-sm ${
                                                      conflict
                                                        ? is1on1Conflict
                                                          ? "bg-orange-50 border-orange-300"
                                                          : "bg-red-50 border-red-300"
                                                        : "bg-green-50 border-green-300"
                                                    }`}
                                                  >
                                                    <div
                                                      className={
                                                        "flex items-start justify-between gap-2"
                                                      }
                                                    >
                                                      <div
                                                        className={"space-y-1"}
                                                      >
                                                        <span
                                                          className={
                                                            "font-medium"
                                                          }
                                                        >
                                                          {formatDate(start, {
                                                            withWeekday: true,
                                                          })}{" "}
                                                          {start.format(
                                                            "HH:mm",
                                                          )}
                                                          –{end.format("HH:mm")}
                                                        </span>
                                                        {slot.teacherConflictingClass && (
                                                          <p
                                                            className={`text-xs ${slot.teacherConflictingClass.is1on1 ? "text-orange-700" : "text-red-600"}`}
                                                          >
                                                            {slot
                                                              .teacherConflictingClass
                                                              .is1on1
                                                              ? "Conflitto teacher 1to1: "
                                                              : "Conflitto teacher: "}
                                                            {dayjs(
                                                              slot
                                                                .teacherConflictingClass
                                                                .startedAtUtc,
                                                            )
                                                              .tz(PLAN_TIMEZONE)
                                                              .format("HH:mm")}
                                                            –
                                                            {dayjs(
                                                              slot
                                                                .teacherConflictingClass
                                                                .endedAtUtc,
                                                            )
                                                              .tz(PLAN_TIMEZONE)
                                                              .format(
                                                                "HH:mm",
                                                              )}{" "}
                                                            con{" "}
                                                            {slot.teacherConflictingClass.participants
                                                              .map(
                                                                (p) =>
                                                                  `${p.givenName} ${p.familyName}`,
                                                              )
                                                              .join(", ")}
                                                          </p>
                                                        )}
                                                        {slot.userConflictingClass && (
                                                          <p
                                                            className={`text-xs ${slot.userConflictingClass.is1on1 ? "text-orange-700" : "text-red-600"}`}
                                                          >
                                                            {slot
                                                              .userConflictingClass
                                                              .is1on1
                                                              ? "Conflitto studente 1to1: "
                                                              : "Conflitto studente: "}
                                                            {dayjs(
                                                              slot
                                                                .userConflictingClass
                                                                .startedAtUtc,
                                                            )
                                                              .tz(PLAN_TIMEZONE)
                                                              .format("HH:mm")}
                                                            –
                                                            {dayjs(
                                                              slot
                                                                .userConflictingClass
                                                                .endedAtUtc,
                                                            )
                                                              .tz(PLAN_TIMEZONE)
                                                              .format("HH:mm")}
                                                          </p>
                                                        )}
                                                      </div>
                                                      <button
                                                        onClick={() =>
                                                          setCheckedSlots(
                                                            (prev) =>
                                                              prev.filter(
                                                                (_, idx) =>
                                                                  idx !== i,
                                                              ),
                                                          )
                                                        }
                                                        className={
                                                          "text-red-400 hover:text-red-600 text-lg leading-none shrink-0"
                                                        }
                                                        title="Rimuovi slot"
                                                      >
                                                        ×
                                                      </button>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>

                                            {addError && (
                                              <p
                                                className={
                                                  "text-red-500 text-sm"
                                                }
                                              >
                                                {addError}
                                              </p>
                                            )}

                                            <div
                                              className={
                                                "flex items-center gap-2 flex-wrap"
                                              }
                                            >
                                              <button
                                                onClick={() => {
                                                  setAddStep("draft");
                                                  setAddError(null);
                                                }}
                                                className={
                                                  "text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded px-3 py-1"
                                                }
                                              >
                                                ← Modifica
                                              </button>
                                              {hasConflicts && (
                                                <button
                                                  disabled={substituting}
                                                  onClick={() =>
                                                    handleAutoSubstitution(plan)
                                                  }
                                                  className={
                                                    "text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded px-3 py-1 disabled:opacity-40"
                                                  }
                                                >
                                                  {substituting
                                                    ? "Risoluzione in corso..."
                                                    : "Risoluzione automatica conflitti"}
                                                </button>
                                              )}
                                              <PrimaryButton
                                                label={
                                                  addingLessons
                                                    ? "Invio in coda..."
                                                    : `Conferma ${checkedSlots.length} lezione${checkedSlots.length !== 1 ? "i" : ""}`
                                                }
                                                loading={addingLessons}
                                                disabled={
                                                  hasConflicts || addingLessons
                                                }
                                                onClick={() =>
                                                  handleConfirmLessons(plan)
                                                }
                                              />
                                            </div>

                                            <SubstitutionResultModal
                                              show={showSubstitutionModal}
                                              result={substitutionResult}
                                              onClose={() =>
                                                handleSubstitutionModalClose(
                                                  plan,
                                                )
                                              }
                                            />
                                          </>
                                        );
                                      })()}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <div className={"flex justify-center"}>
                <InfoFeedback
                  title={"No 1 on 1 plans"}
                  message={`${user.givenName} has no 1 on 1 plans.`}
                />
              </div>
            </Card>
          )}
        </div>
      </Layout>

      {showAddSlotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowAddSlotModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4 space-y-4">
            <h3 className="text-base font-semibold">
              Aggiungi slot (durata 1 ora)
            </h3>
            <DateTimeInput
              hourFromTo={{ from: 7, to: 22 }}
              value={newSlotStart}
              onChange={(v) => v && setNewSlotStart(v)}
            />
            <div className="flex gap-2 justify-end">
              <div className="w-fit">
                <OutlinedButton
                  label="Annulla"
                  onClick={() => setShowAddSlotModal(false)}
                />
              </div>
              <div className="w-fit">
                <PrimaryButton
                  label="Aggiungi"
                  onClick={handleAddSlotFromModal}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
